import { Service, computed, inject, signal } from '@angular/core';
import { catchError, forkJoin, map, of } from 'rxjs';
import { CURRENT_USER_VENDOR_ID } from '../constants/vendor-id';
import type { BaseEntry } from '../models/panel-entry.model';
import type { VendorProfileData } from '../models/vendor-profile.model';
import { evaluate, requiredPanels, type PanelStatus } from '../panels/evaluate';
import { EMPTY_PANEL_DATA, type PanelData, type PanelKey } from '../panels/panel-registry';
import { toVendor, type Vendor } from '../panels/panel-rules';
import { ProfilePanelService } from '../services/profile-panel.service';
import { resolveEntities } from '../utils/resolve-entities.util';

/**
 * State kelengkapan profil vendor: hasil 7 endpoint panel + hasil evaluasinya.
 *
 * `autoProvided: false` + di-provide di route shell (`vendor.routes.ts`) bersama
 * `VendorProfileStore`: `allComplete()` menjadi gerbang tombol Submit Submission di shell, jadi
 * state ini harus hidup selama halaman profil aktif — bukan mati tiap kali user pindah tab
 * (PLAN-PROFILE-TAB.md §D9).
 *
 * Penulis HANYA shell (`vendor-profile.ts`); tab hanya membaca — satu sumber kebenaran.
 */
@Service({ autoProvided: false })
export class ProfileCompletenessStore {
  private readonly api = inject(ProfilePanelService);

  private readonly vendorSig = signal<Vendor | null>(null);
  private readonly dataSig = signal<PanelData>(EMPTY_PANEL_DATA);
  private readonly loadingSig = signal<boolean>(false);
  private readonly partialSig = signal<boolean>(false);

  readonly loading = this.loadingSig.asReadonly();

  /**
   * ≥1 endpoint panel gagal. Tab menampilkan catatan; badge panel yang terdampak tetap ⚠️ karena
   * datanya kosong (fail-safe — user tidak boleh submit dengan kelengkapan yang tak terverifikasi).
   */
  readonly partial = this.partialSig.asReadonly();

  /**
   * Dihitung ulang HANYA saat `vendor` atau `data` berubah — bukan tiap render. Jangan pernah
   * memanggil `evaluate()` dari ekspresi template.
   */
  private readonly result = computed(() => {
    const vendor = this.vendorSig();
    if (!vendor) return { panels: [] as readonly PanelStatus[], allComplete: false };
    return evaluate({ vendor, data: this.dataSig() });
  });

  readonly panels = computed(() => this.result().panels);
  readonly allComplete = computed(() => this.result().allComplete);

  /**
   * Ambil semua panel WAJIB untuk vendor ini, lalu normalisasi + resolve sekali di sini.
   *
   * Dipanggil setelah `current_user` sukses (bukan paralel dengannya): panel MANA yang di-fetch
   * bergantung pada `legal_org_type.ident_name` — vendor Yayasan tidak memicu request izin usaha.
   * Ketujuh request panelnya sendiri tetap paralel satu sama lain.
   */
  load(profile: VendorProfileData): void {
    const vendor = toVendor(profile);
    this.vendorSig.set(vendor);

    const targets = requiredPanels(vendor).filter((p) => p.endpoint !== null);
    if (targets.length === 0) {
      this.dataSig.set(EMPTY_PANEL_DATA);
      return;
    }

    this.loadingSig.set(true);
    this.partialSig.set(false);

    forkJoin(
      targets.map((p) =>
        this.api.getPanel(p.endpoint as string, CURRENT_USER_VENDOR_ID).pipe(
          map(
            (res) =>
              [p.key, resolveEntities(res.data, p.normalize!)] as readonly [
                PanelKey,
                readonly BaseEntry[],
              ],
          ),
          // Gagalnya SATU panel tidak boleh menjatuhkan enam lainnya. Errornya sudah ditoast
          // interceptor global — di sini cukup tandai `partial` dan biarkan panelnya kosong.
          catchError(() => {
            this.partialSig.set(true);
            return of([p.key, []] as readonly [PanelKey, readonly BaseEntry[]]);
          }),
        ),
      ),
    ).subscribe((entries) => {
      this.dataSig.set({ ...EMPTY_PANEL_DATA, ...Object.fromEntries(entries) } as PanelData);
      this.loadingSig.set(false);
    });
  }

  /** Kembalikan ke keadaan awal saat profil gagal dimuat — jangan biarkan hasil lama tertinggal. */
  reset(): void {
    this.vendorSig.set(null);
    this.dataSig.set(EMPTY_PANEL_DATA);
    this.partialSig.set(false);
    this.loadingSig.set(false);
  }
}
